import { defineComponent } from 'vue';
import { QBtn, QIcon, QCard, QList, QItem, QItemSection, QItemLabel, QMenu, QTabPanels, QTabPanel, QImg } from 'quasar';
import utils from '../../../Shared/utils.jsx';
import BaseComponentMixin from './BaseComponentMixin.jsx';
import '../css/Dash.css';
import ResponsiveDash from '../vuecontrols/responsiveDash.vue';
import BrokenImage from '../../../Assets/BrokenImage.svg';
import TranslationContainer from '../TranslationContainer.jsx';

const defaultLayout = () => ({
    breakpoint: 'xl',
    numberOfCols: 24,
    compact: true,
    items: [],
});

export default defineComponent({
    name: 'dash-dsgn',
    mixins: [BaseComponentMixin],
    components: {
        QBtn,
        QIcon,
        QCard,
        QList,
        QItem,
        QItemSection,
        QItemLabel,
        QMenu,
        QTabPanels,
        QTabPanel,
        QImg,
        ResponsiveDash,
        TranslationContainer,
    },
    data() {
        return {
            istemplate: null,
            templateid: null,
            dashboard: 0,
            dash_tabs: [],
            dash_loading: true,
            dashboardMenuOpen: false,
        };
    },
    computed: {
        isTemplateComputed() {
            try {
                if (this.istemplate === null && this.controlData?.IsTemplate != null)
                    this.istemplate = utils.compile(this, this.controlData.IsTemplate);

                if (this.istemplate == null)
                    return false;

                return utils.evaluate(this.istemplate, this) || false;
            }
            catch (e) {
                utils.warn('Template Expression could not evaluate expression: ' + this.controlData?.IsTemplate + '; ' + e);
                return false;
            }
        },
        shellStyle() {
            return {
                display: 'flex',
                'flex-direction': 'column',
                ...utils.getSize(this.controlData?.SizeOptions, this.parentType, this.$parent),
                ...utils.getStyleHints(this.controlData?.StyleHints, this),
            };
        },
    },
    watch: {
        'controlData.DefaultTemplates': {
            deep: true,
            handler() {
                this.reloadTabs();
            },
        },
        'controlData.AllowPersonalDashboards'() {
            this.reloadTabs();
        },
        'controlData.IsTemplate'() {
            this.istemplate = null;
            this.templateid = null;
            this.reloadTabs();
        },
        'controlData.TemplateId'() {
            this.templateid = null;
            this.reloadTabs();
        },
    },
    mounted() {
        this.reloadTabs();
    },
    methods: {
        async reloadTabs() {
            const initial = this.dash_tabs.length === 0;
            if (initial)
                this.dash_loading = true;
            try {
                await this.Refresh();
                if (this.dashboard >= this.dash_tabs.length)
                    this.dashboard = Math.max(0, this.dash_tabs.length - 1);
            }
            finally {
                this.dash_loading = false;
            }
        },
        async Refresh() {
            this.templateid = null;
            if (this.isTemplateComputed && this.controlData?.TemplateId)
                this.templateid = utils.compile(this, this.controlData.TemplateId);

            try {
                let data;
                const templateId = this.templateid ? utils.evaluate(this.templateid, this) : 0;
                if (this.isTemplateComputed) {
                    if (templateId) {
                        data = await utils.api.get(`Apps/Platform/Schema/Data/v1/DashboardTemplate?ID=${templateId}`, false, false);
                        data = [data];
                    }
                    else {
                        data = [{
                            Name: '{Template Name}',
                            Layout: defaultLayout(),
                        }];
                    }
                }
                else {
                    data = await utils.api.request(
                        'POST',
                        `Apps/Data/v1/UserDashboards/ListAllMyDashboards?AllowPersonalDashboards=${this.controlData.AllowPersonalDashboards}`,
                        this.controlData.DefaultTemplates,
                        false,
                        true,
                    );
                    data = data.Result;
                }

                if (data && data.length > 0) {
                    const base = defaultLayout();
                    this.dash_tabs = data
                        .sort((a, b) => (a.Order ?? 0) - (b.Order ?? 0))
                        .map((d) => {
                            const L = d.Layout && typeof d.Layout === 'object' ? d.Layout : {};
                            return {
                                Title: d.Name,
                                Description: d.Description,
                                ID: d.ID,
                                editing: false,
                                layout: {
                                    ...base,
                                    ...L,
                                    items: Array.isArray(L.items) ? L.items : [],
                                },
                                TemplateID: d.TemplateID,
                            };
                        });
                }
                else
                    this.dash_tabs = [];
            }
            catch (e) {
                utils.warn('Failed to load dashboards (designer Dash)', e);
                this.dash_tabs = [];
            }
        },
        getHeader() {
            const header = [];
            if (!this.controlData?.HeaderControls?.length)
                return header;

            for (let i = 0; i < this.controlData.HeaderControls.length; i++) {
                const c = this.controlData.HeaderControls[i];
                let DynamicControl = utils.getDesignerComponent(c);
                if (!c.$objectId)
                    c.$objectId = utils.generateUUID();

                header.push(
                    <DynamicControl
                        key={c.$objectId}
                        type={c.ControlType}
                        name={c.ControlData ? (c.ControlData.Name || 'unnamed') : 'unnamed'}
                        root={this.root}
                        designmodel={c}
                        parentType="HorizontalStack"
                        controlData={c.ControlData}
                        controlName={c.Name}
                        accessPermission={c.AccessPermission}
                    />,
                );
            }
            return header;
        },
        openCardControl(e, controlUrl) {
            if (!controlUrl || !this.selected$)
                return;
            this.Root.ExternalNavigateToName('UserControl', controlUrl);
            e.cancelBubble = true;
            e.stopPropagation();
        },
    },
    render() {
        if (!this.controlData)
            return null;
        if (this.dash_loading && this.dash_tabs.length === 0)
            return null;

        const header = this.getHeader();
        const closeDashboardMenu = () => {
            this.dashboardMenuOpen = false;
        };
        const handleMenuItemClick = (index) => {
            this.dashboard = index;
            closeDashboardMenu();
        };

        const dashboardListItems = this.dash_tabs.map((d, index) => (
            <QItem
                key={index}
                active={this.dashboard === index}
                active-class="text-primary"
                clickable
                onClick={() => handleMenuItemClick(index)}
            >
                <QItemSection avatar>
                    <QIcon name="mdi-view-dashboard" />
                </QItemSection>
                <QItemSection>
                    <QItemLabel>
                        <TranslationContainer context={this} value={d.Title} />
                    </QItemLabel>
                </QItemSection>
            </QItem>
        ));

        const window_body = [];
        for (let i = 0; i < this.dash_tabs.length; i++) {
            const t = this.dash_tabs[i];
            const rawLayout = t.layout && typeof t.layout === 'object' ? t.layout : {};
            const layout = {
                ...defaultLayout(),
                ...rawLayout,
                items: Array.isArray(rawLayout.items) ? rawLayout.items : [],
            };

            const scopedSlots = {
                content: ({ item }) => {
                    if (item.ControlURL === 'ReportUnavailable') {
                        return (
                            <div style="align-self:center;">
                                <QImg style="transform: scale(.75)" src={BrokenImage} />
                                <TranslationContainer context={this} value="This report has been removed" />
                                <br />
                                <TranslationContainer context={this} value="from the Card Library" />
                            </div>
                        );
                    }
                    if (item.ControlURL) {
                        const openTitle = `Open ${item.ControlURL}`;
                        return (
                            <div
                                class="c-Dash-Content"
                                style={{
                                    position: 'relative',
                                    flexGrow: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '12px',
                                    color: '#616161',
                                }}
                            >
                                <div
                                    class="design-options designer-container-selected-clickableitem"
                                    title={openTitle}
                                    style={{
                                        top: '4px',
                                        left: '4px',
                                        display: this.selected$ ? 'block' : 'none',
                                        zIndex: 40,
                                        pointerEvents: this.selected$ ? 'auto' : 'none',
                                    }}
                                    onClick={(e) => this.openCardControl(e, item.ControlURL)}
                                >
                                    Open
                                </div>
                                <QIcon name="mdi-monitor-dashboard" size="lg" class="q-mb-sm" />
                                <div style="font-weight: 600;">{item.title || 'Card'}</div>
                                <div style="font-size: 12px; word-break: break-all;">{item.ControlURL}</div>
                                <div style="font-size: 11px; margin-top: 8px;">
                                    <TranslationContainer context={this} value="Preview" />
                                </div>
                            </div>
                        );
                    }
                    return (
                        <div style="padding: 8px;">
                            <TranslationContainer context={this} value="No content defined" />
                            {`: ${item.id}`}
                        </div>
                    );
                },
            };

            window_body.push(
                <QTabPanel key={i} name={i} style="overflow-y: scroll; flex-grow: 1;">
                    <ResponsiveDash layout={layout} editing={false} root={this.root}>
                        {scopedSlots}
                    </ResponsiveDash>
                </QTabPanel>,
            );
        }

        const inner = this.dash_tabs.length
            ? (
                <div style="display:flex; flex-grow:1; flex-direction: column;">
                    <div style="display: grid; grid-template-columns: 1fr 1fr 1fr;" class="mx-2">
                        <div>
                            <div>
                                <QBtn color="primary" outline no-caps class="align-self-center q-mx-md q-my-sm">
                                    {this.dash_tabs[this.dashboard]?.Title}
                                    <QIcon style={{ marginLeft: '8px' }} name="mdi-menu-down" />
                                    <QMenu
                                        anchor="bottom left"
                                        self="top left"
                                        modelValue={this.dashboardMenuOpen}
                                        onUpdate:modelValue={(v) => { this.dashboardMenuOpen = v; }}
                                    >
                                        <QList>{dashboardListItems}</QList>
                                    </QMenu>
                                </QBtn>
                            </div>
                        </div>
                        <div />
                        <div style="padding-right: 10px; display:flex; flex-direction:row; gap: 5px; align-items:center; justify-content:flex-end">
                            <div style="display:flex; flex-direction:row; gap: 5px; align-items:center">{header}</div>
                        </div>
                    </div>
                    <QCard style="display:flex; flex-grow:1" class="flex-grow">
                        <QTabPanels
                            modelValue={this.dashboard}
                            onUpdate:modelValue={(v) => { this.dashboard = v; }}
                            style="display:flex; flex-grow: 1;"
                            animated
                        >
                            {window_body}
                        </QTabPanels>
                    </QCard>
                </div>
            )
            : (
                <div style="display: grid; grid-template-rows: 1fr 1fr 1fr; height: 100%">
                    <QIcon name="mdi-view-dashboard-edit-outline" style="font-size:20vh; top: 80px; color: #BDBDBD; justify-self: center" />
                    <div style="display:flex; flex-direction:column; justify-content:center; align-items:center; flex-grow:1; gap:10px;">
                        <div>
                            <b style="font-size: 24px">
                                <TranslationContainer context={this} value="Let's get started!" />
                            </b>
                        </div>
                        <div>
                            <TranslationContainer context={this} value="Visualize data in one place by creating dashboards that show what is happening across your contact center in real-time" />
                        </div>
                        <QBtn color="primary" disabled no-caps class="c-SentenceCaseButton">
                            <TranslationContainer context={this} value="CREATE DASHBOARD" />
                        </QBtn>
                    </div>
                </div>
            );

        return (
            <div
                class={{
                    'designer-container': true,
                    selected: this.selected$,
                    'q-ma-sm': true,
                    'c-Dash': true,
                    [`c-name-${this.name || 'unnamed'}`]: true,
                }}
                style={this.shellStyle}
                onClick={(e) => this.handleClick(e)}
            >
                <div class={{ 'designer-container-hovering': true, 'designer-container-selected': this.selected$ }} />
                {inner}
            </div>
        );
    },
});
